import { useId, useState } from "react";
import { useFormContext } from "react-hook-form";

import {
  TEXT_MAX,
  answerKey,
  answerProblem,
  isCurrency,
  splitUnit,
  unitOf,
  visibleQuestions,
} from "../lib/questions";
import { ChoiceGroup, FieldError } from "./fields";

const YES_NO = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const optionsOf = (question) => (question.options || []).map((o) => ({ value: o.id, label: o.text }));

const HINTS = {
  multiple_choice: "Choose all that apply.",
  experience_years: "Whole or half years, like 2 or 1.5.",
};

// What the applicant may type: digits only for numbers, digits and one
// decimal point for years. Currency amounts show with thousands separators.
const cleanNumber = (raw, type) => {
  if (type !== "experience_years") return raw.replace(/\D/g, "");
  const [whole, ...rest] = raw.replace(/[^\d.]/g, "").split(".");
  return rest.length ? `${whole}.${rest.join("").slice(0, 1)}` : whole;
};

function QuestionLabel({ htmlFor, position, label, required, as: Tag = "label" }) {
  return (
    <Tag className="cr-label" htmlFor={htmlFor}>
      <span className="cr-q-number">{position}</span>
      {label}
      {!required && <span className="cr-optional"> (optional)</span>}
    </Tag>
  );
}

function NumberAnswer({ question, label, value, onChange, error }) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const unit = unitOf(question);
  const money = isCurrency(unit);
  const shown = money && value ? Number(value).toLocaleString("en-US") : value ?? "";
  const hint = HINTS[question.question_type];

  return (
    <div className="cr-field">
      {label(id)}
      {hint && (
        <p className="cr-hint" id={hintId}>
          {hint}
        </p>
      )}
      <div className="cr-with-unit">
        <input
          id={id}
          className="cr-input"
          type="text"
          inputMode={question.question_type === "experience_years" ? "decimal" : "numeric"}
          autoComplete="off"
          value={shown}
          aria-invalid={Boolean(error)}
          aria-describedby={[hint && hintId, error && errorId].filter(Boolean).join(" ") || undefined}
          onChange={(e) => onChange(cleanNumber(e.target.value, question.question_type))}
        />
        {unit && (
          <span className="cr-unit" aria-hidden="true">
            {unit}
          </span>
        )}
      </div>
      <FieldError id={errorId} message={error} />
    </div>
  );
}

function TextAnswer({ label, value, onChange, error }) {
  const id = useId();
  const errorId = `${id}-error`;
  const length = (value || "").length;
  return (
    <div className="cr-field">
      {label(id)}
      <textarea
        id={id}
        className="cr-textarea"
        rows={3}
        maxLength={TEXT_MAX}
        value={value ?? ""}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="cr-field-foot">
        <FieldError id={errorId} message={error} />
        <span className="cr-counter" aria-hidden="true">
          {length} / {TEXT_MAX}
        </span>
      </div>
    </div>
  );
}

function ChoiceAnswer({ question, legend, value, onChange, error }) {
  const options = question.question_type === "boolean" ? YES_NO : optionsOf(question);
  return (
    <ChoiceGroup
      legend={legend}
      hint={HINTS[question.question_type]}
      name={answerKey(question.id)}
      options={options}
      value={value}
      onChange={onChange}
      error={error}
      multiple={question.question_type === "multiple_choice"}
      columns={options.length === 2 ? 2 : undefined}
    />
  );
}

function QuestionInput({ question, position, value, onChange, error }) {
  const { label } = splitUnit(question.text);
  const labelProps = { position, label, required: question.is_required };
  const labelFor = (htmlFor) => <QuestionLabel htmlFor={htmlFor} {...labelProps} />;

  switch (question.question_type) {
    case "boolean":
    case "single_choice":
    case "multiple_choice":
      return (
        <ChoiceAnswer
          question={question}
          legend={<QuestionLabel as="span" {...labelProps} />}
          value={value}
          onChange={onChange}
          error={error}
        />
      );
    case "text":
      return <TextAnswer label={labelFor} value={value} onChange={onChange} error={error} />;
    default:
      return (
        <NumberAnswer question={question} label={labelFor} value={value} onChange={onChange} error={error} />
      );
  }
}

// Errors show for every question after a Continue attempt, and for a
// question as soon as the applicant has touched it and left it wrong.
export default function QuestionsStep({ questions, showErrors }) {
  const { watch, setValue } = useFormContext();
  const [touched, setTouched] = useState({});
  const answers = watch("answers") || {};
  const shown = visibleQuestions(questions, answers);

  return (
    <div className="cr-questions">
      {shown.map((question, i) => {
        const key = answerKey(question.id);
        const showProblem = showErrors || (touched[key] && answers[key] !== undefined);
        const error = showProblem ? answerProblem(question, answers[key]) : null;
        return (
          <div
            key={question.id}
            className="cr-question"
            data-invalid={Boolean(error)}
            onBlur={() => setTouched((t) => ({ ...t, [key]: true }))}
          >
            <QuestionInput
              question={question}
              position={`Question ${i + 1} of ${shown.length}`}
              value={answers[key]}
              onChange={(v) => setValue(`answers.${key}`, v, { shouldDirty: true })}
              error={error}
            />
          </div>
        );
      })}
    </div>
  );
}
